import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, Search, X, ChevronRight, ChevronDown, Clock, User, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { UserSearchInput } from '@/components/tasks/UserSearchInput';
import { taskTemplateService } from '@/lib/api/taskTemplate.service';
import type { TaskTemplate } from '@/types/taskTemplate.types';
import type { UserContact } from '@/types/user-contact.types';

interface Props {
  open: boolean;
  onClose: () => void;
  /** If provided, templates are ranked by tag overlap with this celebration */
  celebrationId?: number;
  /** If provided, passed to the apply payload */
  eventId?: number;
  /** ISO date string — used to compute due_date from template offset */
  eventDate?: string;
  onApplied: () => void;
}

const PRIORITY_STYLES: Record<string, string> = {
  high:   'bg-red-100 text-red-700',
  medium: 'bg-amber-100 text-amber-700',
  low:    'bg-green-100 text-green-700',
};

function offsetLabel(offset: number | undefined): string | null {
  if (offset === undefined || offset === null) return null;
  if (offset === 0) return 'Day-of event';
  if (offset < 0) return `${Math.abs(offset)} day${Math.abs(offset) > 1 ? 's' : ''} before`;
  return `${offset} day${offset > 1 ? 's' : ''} after`;
}

export const TaskTemplatePicker: React.FC<Props> = ({
  open,
  onClose,
  celebrationId,
  eventId,
  eventDate,
  onApplied,
}) => {
  const { t } = useTranslation('events');
  const [templates, setTemplates] = useState<TaskTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [assignedUser, setAssignedUser] = useState<UserContact | null>(null);
  const [applyDate, setApplyDate] = useState(eventDate || '');
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const data = celebrationId
        ? await taskTemplateService.getSuggestions(celebrationId)
        : await taskTemplateService.getAll();
      setTemplates(data);
    } catch {
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [celebrationId]);

  useEffect(() => {
    if (open) {
      loadTemplates();
      setSearch('');
      setExpandedId(null);
      setAssignedUser(null);
      setApplyDate(eventDate || '');
      setApplyError(null);
    }
  }, [open, loadTemplates, eventDate]);

  const filteredTemplates = templates.filter(t => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      t.title.toLowerCase().includes(q) ||
      t.description?.toLowerCase().includes(q) ||
      t.role_hint?.toLowerCase().includes(q)
    );
  });

  const handleToggleExpand = (templateId: number) => {
    setExpandedId(prev => (prev === templateId ? null : templateId));
    setAssignedUser(null);
    setApplyError(null);
  };

  const handleApply = async (template: TaskTemplate) => {
    if (!assignedUser) {
      setApplyError(t('taskTemplatePicker.errors.selectPerson'));
      return;
    }
    if (!celebrationId) {
      setApplyError(t('taskTemplatePicker.errors.noCelebrationContext'));
      return;
    }

    setApplying(true);
    setApplyError(null);
    try {
      await taskTemplateService.apply(template.id, {
        celebration_id: celebrationId,
        event_id: eventId,
        event_date: applyDate || undefined,
        assigned_to: assignedUser.id,
      });
      onApplied();
      onClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : t('taskTemplatePicker.errors.applyTemplate');
      setApplyError(msg);
    } finally {
      setApplying(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-lg shadow-xl flex flex-col max-h-[85vh]">

        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b shrink-0">
          <h3 className="font-semibold text-sm flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-indigo-500" />
            {t('taskTemplatePicker.title')}
          </h3>
          <Button variant="ghost" size="icon-sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Search */}
        <div className="p-3 border-b shrink-0">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t('taskTemplatePicker.searchPlaceholder')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-8"
              compact
            />
          </div>
          {celebrationId && (
            <p className="text-xs text-muted-foreground mt-1.5">
              {t('taskTemplatePicker.rankedHint')}
            </p>
          )}
        </div>

        {/* Template list */}
        <div className="overflow-y-auto flex-1 p-3 space-y-2">
          {loading && (
            <div className="text-sm text-muted-foreground text-center py-6 animate-pulse">
              {t('taskTemplatePicker.loadingTemplates')}
            </div>
          )}

          {!loading && filteredTemplates.length === 0 && (
            <div className="text-center py-8">
              <p className="text-sm font-medium text-muted-foreground">{t('taskTemplatePicker.noTemplates')}</p>
              {search && (
                <p className="text-xs text-muted-foreground mt-1">{t('taskTemplatePicker.tryDifferentSearch')}</p>
              )}
            </div>
          )}

          {!loading && filteredTemplates.map(template => {
            const isExpanded = expandedId === template.id;
            const offset = offsetLabel(template.due_days_offset);
            return (
              <div
                key={template.id}
                className="rounded-lg border border-gray-200 hover:border-indigo-300 bg-white transition-colors"
              >
                {/* Card header */}
                <div className="flex items-start justify-between p-3 gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-gray-800">{template.title}</span>
                      <Badge
                        className={`text-[10px] h-4 px-1.5 font-medium border-0 ${PRIORITY_STYLES[template.priority]}`}
                      >
                        {template.priority}
                      </Badge>
                      {offset && (
                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          {offset}
                        </span>
                      )}
                      {template.role_hint && (
                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                          <User className="h-3 w-3" />
                          {template.role_hint}
                        </span>
                      )}
                    </div>

                    {template.description && (
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                        {template.description}
                      </p>
                    )}
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    className="shrink-0 gap-1 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                    onClick={() => handleToggleExpand(template.id)}
                  >
                    {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    {isExpanded ? t('common:actions.cancel') : t('taskTemplatePicker.use')}
                  </Button>
                </div>

                {/* Apply panel */}
                {isExpanded && (
                  <div className="border-t bg-indigo-50/50 p-3 space-y-3">
                    {/* Event date override (only if template has an offset) */}
                    {template.due_days_offset !== undefined && template.due_days_offset !== null && (
                      <div>
                        <Label className="text-xs">
                          {t('taskTemplatePicker.eventDate')}
                          <span className="text-muted-foreground ml-1">{t('taskTemplatePicker.eventDateHint')}</span>
                        </Label>
                        <Input
                          type="date"
                          value={applyDate ? applyDate.slice(0, 10) : ''}
                          onChange={e => setApplyDate(e.target.value)}
                          compact
                          className="mt-1"
                        />
                      </div>
                    )}

                    <div>
                      <Label className="text-xs">{t('taskTemplatePicker.assignTo')}</Label>
                      <UserSearchInput
                        selectedUser={assignedUser}
                        onUserSelect={setAssignedUser}
                        placeholder={t('taskTemplatePicker.searchPerson')}
                        celebrationId={celebrationId}
                        filterTeamMembers={!!celebrationId}
                        className="mt-1"
                      />
                    </div>

                    {applyError && (
                      <div className="flex items-start gap-2 text-xs text-red-600 bg-red-50 border border-red-100 rounded p-2">
                        <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                        {applyError}
                      </div>
                    )}

                    <Button
                      size="sm"
                      className="w-full"
                      onClick={() => handleApply(template)}
                      disabled={applying || !assignedUser}
                    >
                      {applying ? t('taskTemplatePicker.creatingTask') : t('taskTemplatePicker.createTaskFromTemplate')}
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
